import { Injectable, OnDestroy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription, Subject } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import moment, { Moment } from 'moment';

type DraftPayload<T = any> = {
  version: number;
  savedAt: string;
  value: T;
  meta?: Record<string, any>;
};

@Injectable({ providedIn: 'root' })
export class FormDraftService implements OnDestroy {
  private bindings = new Map<string, Subscription>();
  private teardown$ = new Subject<void>();

  private static readonly LAST_CLEANUP_KEY = '__formDraft:lastCleanupISO';
  private static readonly CLEANUP_INTERVAL_MIN = 60;

  bindForm<T extends Record<string, any>>(
    key: string,
    form: FormGroup,
    options?: {
      version?: number;
      debounceMs?: number;
      meta?: Record<string, any>;
      restoreStrategy?: 'patch' | 'set';
      onlyDirty?: boolean;
      pruneUndefined?: boolean;
      reviveMomentKeys?: string[];
      retentionDays?: number;
      maxDraftsPerPrefix?: number;
    }
  ): void {
    const {
      version = 1,
      debounceMs = 400,
      meta,
      restoreStrategy = 'patch',
      onlyDirty = false,
      pruneUndefined = true,
      reviveMomentKeys = [],
      retentionDays = 7,
      maxDraftsPerPrefix = 100,
    } = options || {};

    this.maybeCleanupNamespace(this.extractPrefix(key), retentionDays, maxDraftsPerPrefix);

    const restored = this.restore<T>(key, version);
    if (restored) {
      const method = restoreStrategy === 'set' ? 'setValue' : 'patchValue';
      const revived = this.reviveMomentKeys(restored.value as Record<string, any>, reviveMomentKeys);
      try {
        (form[method] as (value: T, opts?: any) => void)(revived as T, { emitEvent: false });
        form.markAsDirty();
      } catch (e) {
        console.warn('[FormDraftService] restore failed:', key, e);
      }
    }

    const sub = form.valueChanges
      .pipe(auditTime(debounceMs))
      .subscribe(() => {
        if (onlyDirty && !form.dirty) return;
        if (!this.isFormPopulated(form)) return;

        const cleaned = pruneUndefined ? this.pruneUndefined(form.value) : form.value;
        const serializable = this.serializeDates(cleaned);
        const payload: DraftPayload<T> = {
          version,
          savedAt: moment().toISOString(true),
          value: serializable as T,
          meta,
        };
        this.safeWrite(key, payload);
      });

    this.unbind(key);
    this.bindings.set(key, sub);

    const beforeUnload = () => {
      try {
        const cleaned = pruneUndefined ? this.pruneUndefined(form.value) : form.value;
        const serializable = this.serializeDates(cleaned);
        const payload: DraftPayload<T> = {
          version,
          savedAt: moment().toISOString(true),
          value: serializable as T,
          meta,
        };
        this.safeWrite(key, payload);
      } catch {}
    };
    window.addEventListener('beforeunload', beforeUnload);
    const cleanupSub = new Subscription(() => window.removeEventListener('beforeunload', beforeUnload));
    this.bindings.set(`${key}__cleanup`, cleanupSub);
  }

  clear(key: string): void {
    try { localStorage.removeItem(key); } catch {}
  }

  unbind(key: string): void {
    this.bindings.get(key)?.unsubscribe();
    this.bindings.delete(key);
    this.bindings.get(`${key}__cleanup`)?.unsubscribe();
    this.bindings.delete(`${key}__cleanup`);
  }

  restore<T = any>(key: string, expectedVersion?: number): DraftPayload<T> | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as DraftPayload<T>;
      if (expectedVersion != null && parsed.version !== expectedVersion) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  ngOnDestroy(): void {
    this.teardown$.next();
    this.teardown$.complete();
    this.bindings.forEach(s => s.unsubscribe());
    this.bindings.clear();
  }

  // Helpers

  private isFormPopulated(form: FormGroup): boolean {
    return Object.values(form.controls).some((ctrl: AbstractControl) => {
      const v = ctrl.value;
      return !(v === null || v === undefined || v === '');
    });
  }

  private pruneUndefined<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj)) as T;
  }

  private serializeDates<T>(value: T): T {
    const replacer = (_: string, val: any) => {
      if (moment.isMoment(val)) return (val as Moment).toISOString(true);
      if (val instanceof Date) return moment(val).toISOString(true);
      return val;
    };
    return JSON.parse(JSON.stringify(value, replacer)) as T;
  }

  private reviveMomentKeys(value: Record<string, any>, keys: string[]): Record<string, any> {
    if (!keys?.length) return value;
    const clone: Record<string, any> = { ...value };
    for (const k of keys) {
      const v = clone[k];
      if (typeof v === 'string' && this.isIsoDateString(v)) {
        const m = moment(v);
        if (m.isValid()) clone[k] = m;
      }
    }
    return clone;
  }

  private isIsoDateString(s: string): boolean {
    return /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(s);
    }

  private safeWrite(key: string, payload: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (e: any) {
      if (this.isQuotaExceeded(e)) {
        console.warn('[FormDraftService] quota exceeded, clearing:', key);
        try { localStorage.removeItem(key); } catch {}
      }
    }
  }

  private isQuotaExceeded(e: any): boolean {
    return e && (e.name === 'QuotaExceededError' || e.code === 22);
  }

  // Auto-cleanup

  private extractPrefix(key: string): string {
    const i = key.indexOf(':');
    return i > 0 ? key.substring(0, i + 1) : '';
  }

  private maybeCleanupNamespace(prefix: string, retentionDays: number, maxKeep: number): void {
    try {
      const lastISO = localStorage.getItem(FormDraftService.LAST_CLEANUP_KEY);
      const shouldRun =
        !lastISO || moment().diff(moment(lastISO), 'minutes') >= FormDraftService.CLEANUP_INTERVAL_MIN;
      if (!shouldRun) return;

      this.cleanupNamespace(prefix, retentionDays, maxKeep);
      localStorage.setItem(FormDraftService.LAST_CLEANUP_KEY, moment().toISOString(true));
    } catch {}
  }

  private cleanupNamespace(prefix: string, retentionDays: number, maxKeep: number): void {
    if (!prefix) return;

    const now = moment();
    const recent: Array<{ key: string; savedAt: Moment }> = [];

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(prefix)) continue;

      try {
        const parsed = JSON.parse(localStorage.getItem(k)!);
        const savedAtISO = parsed?.savedAt;
        if (!savedAtISO) continue;

        const savedAt = moment(savedAtISO);
        if (!savedAt.isValid()) { localStorage.removeItem(k); continue; }

        if (now.diff(savedAt, 'days') > retentionDays) {
          localStorage.removeItem(k);
          continue;
        }
        recent.push({ key: k, savedAt });
      } catch {
        // skip
      }
    }

    if (recent.length > maxKeep) {
      recent.sort((a, b) => a.savedAt.valueOf() - b.savedAt.valueOf());
      for (let j = 0; j < recent.length - maxKeep; j++) {
        localStorage.removeItem(recent[j].key);
      }
    }
  }
}
